// src/pages/RapportPaysSoutien.jsx
import React, { useState, useEffect, useMemo } from 'react';
import NavigationBar from '../navbar/NavigationBar';
import DownloadIcon from '@mui/icons-material/Download';
import {
  Container,
  Typography,
  Grid,
  Card,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  styled,
  createTheme,
  ThemeProvider,
  Box,
  CardMedia,
  Button,
} from '@mui/material';
import moment from 'moment';
import Footer from '../navbar/Footer';
import { getPublications } from '../services/publicationService';

const StyledButton = styled(Button)(({ theme }) => ({
  marginTop: '20px',
  backgroundColor: 'green',
  color: 'white',
  '&:hover': {
    backgroundColor: 'darkgreen',
  },
  '&:active': {
    backgroundColor: 'darkgreen',
  },
}));

function RapportPaysSoutien() {
  const [visibleCount, setVisibleCount] = useState(4);
  const [selectedYear, setSelectedYear] = useState('Tous');
  const [rapportsData, setRapportsData] = useState([]);
  const [filteredRapports, setFilteredRapports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Chargement des données depuis l'API
  useEffect(() => {
    setLoading(true);
    getPublications({ type: 'rpss', perPage: 1000 })
      .then((res) => {
        if (res.error) {
          setError(res.error);
        } else {
          // Mapper les données de l'API vers la structure attendue
          const mapped = res.data.map((item) => ({
            title: item.title,
            principalLink: item.document_url,
            image: item.image_url,
            description: item.description,
            date: new Date(item.date), // utilisé pour le tri et le filtre
            // Si l'API fournit un champ pour l'annexe, on peut le mapper ici
            annexeLink: item.annexe_link || null,
          }));
          // Trier par date décroissante (plus récent en premier)
          const sorted = mapped.sort((a, b) => b.date - a.date);
          setRapportsData(sorted);
          setFilteredRapports(sorted);
        }
      })
      .catch((err) => {
        setError('Erreur lors du chargement des données.');
        console.error(err);
      })
      .finally(() => setLoading(false));
  }, []);

  const handleShowMore = () => {
    setVisibleCount((prevCount) => prevCount + 2);
  };

  const handleYearChange = (event) => {
    const year = event.target.value;
    setSelectedYear(year);
    let filtered;
    if (year === 'Tous') {
      filtered = [...rapportsData];
    } else {
      filtered = rapportsData.filter((rapport) => {
        const rapportYear = rapport.date.getFullYear();
        return rapportYear === parseInt(year);
      });
    }
    // Trier par date décroissante
    filtered.sort((a, b) => b.date - a.date);
    setFilteredRapports(filtered);
    // visibleCount reste inchangé (comportement statique)
  };

  const StyledSelect = styled(Select)(({ theme }) => ({
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.primary.main,
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.primary.light,
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.primary.dark,
    },
  }));

  const theme = createTheme();

  // Années disponibles à partir des données chargées
  const availableYears = useMemo(() => {
    const years = rapportsData.map((rapport) => rapport.date.getFullYear());
    return [...new Set(years)].sort((a, b) => b - a);
  }, [rapportsData]);

  if (loading) {
    return (
      <ThemeProvider theme={theme}>
        <div style={{ backgroundColor: theme.palette.background.default, paddingBottom: '32px' }}>
          <NavigationBar />
          <Container maxWidth="lg" style={{ paddingTop: '54px' }}>
            <Typography variant="h4" align="justify" gutterBottom style={{ color: 'green', marginBottom: '16px' }}>
              Rapports Pays sur le Soutien à la Statistique - CRESS Burundi
            </Typography>
            <Divider style={{ margin: '16px 0' }} />
            <Typography variant="body1">Chargement des données...</Typography>
          </Container>
          <Footer />
        </div>
      </ThemeProvider>
    );
  }

  if (error) {
    return (
      <ThemeProvider theme={theme}>
        <div style={{ backgroundColor: theme.palette.background.default, paddingBottom: '32px' }}>
          <NavigationBar />
          <Container maxWidth="lg" style={{ paddingTop: '54px' }}>
            <Typography variant="h4" align="justify" gutterBottom style={{ color: 'green', marginBottom: '16px' }}>
              Rapports Pays sur le Soutien à la Statistique - CRESS Burundi
            </Typography>
            <Divider style={{ margin: '16px 0' }} />
            <Typography variant="body1" color="error">{error}</Typography>
          </Container>
          <Footer />
        </div>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <div style={{ backgroundColor: theme.palette.background.default, paddingBottom: '32px' }}>
        <NavigationBar />
        <Container maxWidth="lg" style={{ paddingTop: '54px' }}>
          <Typography variant="h4" align="justify" gutterBottom style={{ color: 'green', marginBottom: '16px' }}>
            Rapports Pays sur le Soutien à la Statistique - CRESS Burundi
          </Typography>
          <Divider style={{ margin: '16px 0' }} />
          <Typography variant="body1" align="justify" paragraph style={{ marginBottom: '24px' }}>
            Les Rapports Pays sur le Soutien à la Statistique dans le cadre du projet CRESS (Cadre Renforcé de Soutien Statistique) documentent les progrès accomplis dans le renforcement des capacités du système statistique national du Burundi. Ces rapports annuels présentent une analyse détaillée des activités menées, des résultats obtenus et des défis rencontrés dans l'amélioration de la production, diffusion et utilisation des statistiques officielles.
          </Typography>

          <FormControl fullWidth variant="outlined" style={{ marginLeft: '0px', width: '300px' }}>
            <InputLabel id="year-select-label">Filtrer par année</InputLabel>
            <StyledSelect
              labelId="year-select-label"
              id="year-select"
              value={selectedYear}
              onChange={handleYearChange}
              label="Filtrer par année"
            >
              <MenuItem value="Tous">Tous les rapports</MenuItem>
              {availableYears.map((year) => (
                <MenuItem key={year} value={year.toString()}>
                  {year}
                </MenuItem>
              ))}
            </StyledSelect>
          </FormControl>

          <Grid container spacing={1} style={{ marginTop: '5px' }}>
            {filteredRapports.slice(0, visibleCount).map((rapport, index) => (
              <Grid item xs={12} sm={12} key={index}>
                <Card elevation={3} style={{ borderRadius: '12px', overflow: 'hidden', display: 'flex', flexDirection: 'column', height: '100%' }}>
                  <Box sx={{ display: 'flex' }}>
                    <a
                      href={rapport.principalLink}
                      style={{ textDecoration: 'none', color: 'inherit' }}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <CardMedia
                        component="img"
                        sx={{ width: 141, height: 150, m: 2 }}
                        image={rapport.image}
                        alt={rapport.title}
                      />
                    </a>
                    <Box sx={{ display: 'flex', flexDirection: 'column', pl: 2, pt: 2 }}>
                      <Typography variant="h6" component="div" style={{ fontWeight: 'bold', marginBottom: '10px', color: 'green' }}>
                        <a
                          href={rapport.principalLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ textDecoration: 'none', color: 'inherit' }}
                        >
                          <DownloadIcon /> {rapport.title}
                        </a>
                      </Typography>
                      <Typography variant="body2" component="div" style={{ color: '#555', marginBottom: '10px' }}>
                        {rapport.description}
                      </Typography>
                      {rapport.annexeLink && (
                        <Typography variant="body2" style={{ marginBottom: '8px' }}>
                          <a
                            href={rapport.annexeLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ textDecoration: 'none', color: 'green', fontWeight: 'bold' }}
                          >
                            Télécharger les annexes
                          </a>
                        </Typography>
                      )}
                      <Typography variant="caption" color="textSecondary">
                        Publié en {moment(rapport.date).format('MMMM YYYY')}
                      </Typography>
                    </Box>
                  </Box>
                </Card>
              </Grid>
            ))}
          </Grid>

          {visibleCount < filteredRapports.length && (
            <StyledButton onClick={handleShowMore}>
              Plus de rapports
            </StyledButton>
          )}
          <br />
          <br />
          <Typography variant="body1" align="justify" paragraph style={{ marginBottom: '24px', lineHeight: 1.6 }}>
            Ces rapports constituent un outil essentiel pour le suivi et l'évaluation des efforts de renforcement des capacités statistiques au Burundi, permettant d'ajuster les interventions en fonction des besoins identifiés et des priorités nationales.
          </Typography>
        </Container>
        <Footer />
      </div>
    </ThemeProvider>
  );
}

export default RapportPaysSoutien;